export class GameLoop {
    private updateCallback: (deltaTime: number) => void;
    private renderCallback: () => void;
    private isRunning: boolean;
    private isPaused: boolean;
    private lastTime: number;
    private rafId: number | null;
    private visibilityHandler: (() => void) | null = null;

    constructor(updateCallback: (deltaTime: number) => void, renderCallback: () => void) {
        this.updateCallback = updateCallback;
        this.renderCallback = renderCallback;
        this.isRunning = false;
        this.isPaused = false;
        this.lastTime = 0;
        this.rafId = null;

        // Handle page visibility
        this.visibilityHandler = () => {
            if (document.hidden) {
                this.pause();
            } else {
                this.resume();
            }
        };
        document.addEventListener('visibilitychange', this.visibilityHandler);
    }

    start(): void {
        if (this.isRunning) return;

        this.isRunning = true;
        this.isPaused = false;
        this.lastTime = performance.now();
        this.loop(this.lastTime);
    }

    stop(): void {
        this.isRunning = false;
        this.isPaused = false;
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
    }

    pause(): void {
        if (this.isPaused) return;
        this.isPaused = true;
        // pause 시 RAF 중단하여 메모리 누수 방지
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
    }

    resume(): void {
        if (this.isRunning && this.isPaused) {
            this.isPaused = false;
            this.lastTime = performance.now();
            // resume 시 RAF 재시작
            this.loop(this.lastTime);
        }
    }

    destroy(): void {
        this.stop();
        if (this.visibilityHandler) {
            document.removeEventListener('visibilitychange', this.visibilityHandler);
            this.visibilityHandler = null;
        }
    }

    private loop(currentTime: number): void {
        if (!this.isRunning || this.isPaused) return;

        this.rafId = requestAnimationFrame((time) => this.loop(time));

        // Calculate delta time in seconds
        const deltaTime = (currentTime - this.lastTime) / 1000;
        this.lastTime = currentTime;

        // Cap delta time to prevent spiral of death
        // iOS Safari에서 백그라운드 복귀 시 큰 delta 방지
        const cappedDelta = Math.min(deltaTime, 0.05);

        // 비정상적인 delta 무시 (음수 또는 0)
        if (cappedDelta <= 0) return;

        // Update and render in try-catch to prevent crash propagation
        try {
            this.updateCallback(cappedDelta);
            this.renderCallback();
        } catch (e) {
            console.error('Game loop error:', e);
        }
    }
}
